import { useCallback, useState } from 'react';
import { IconName } from 'src/components/ui/Icon/Icon';

const STORAGE_KEY = 'cmdk_recent';
const MAX_RECENT = 6;

export type RecentEntry = {
  to: string;
  label: string;
  subtitle?: string;
  icon: IconName;
};

function read(): RecentEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(0, MAX_RECENT) : [];
  } catch {
    return [];
  }
}

/**
 * Drops the "recently visited" list.
 *
 * Called from `resetClientState()` on logout: the entries carry order numbers,
 * contact and product names the previous user opened — user content, not a
 * device preference.
 */
export function clearRecentlyVisited(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore disabled storage */
  }
}

/**
 * Tracks the last few items opened from the command palette (localStorage). Used
 * as the palette's empty state ("Zuletzt besucht") before the user types.
 */
export function useRecentlyVisited() {
  const [recent, setRecent] = useState<RecentEntry[]>(read);

  const push = useCallback((entry: RecentEntry) => {
    setRecent(prev => {
      const next = [entry, ...prev.filter(e => e.to !== entry.to)].slice(0, MAX_RECENT);
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* ignore quota / disabled storage */
      }
      return next;
    });
  }, []);

  return { recent, push };
}
